import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FilmSlate,
  Play,
  CircleNotch,
  CheckCircle,
  WarningCircle,
  Trash,
  DownloadSimple,
  Clock,
  VideoCamera,
  Image as ImageIcon,
  Sparkle,
  ArrowClockwise,
  MagicWand,
  Link as LinkIcon,
  Upload,
  Lightning,
  MusicNote,
  X,
} from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";

// Magic Hour's renderers cap at 180 s (3 min) of audio per render for BOTH
// lip-sync and talking-photo. The long-form pipeline chunks the script into
// ≤178 s pieces and stitches them so
// users can produce true YouTube-length videos.
export default function LongFormStudio() {
  const [topic, setTopic] = useState("");
  const [customScript, setCustomScript] = useState("");
  const [targetMinutes, setTargetMinutes] = useState(5);
  const [pipelineMode, setPipelineMode] = useState("lip_sync");
  const [images, setImages] = useState([]);
  const [baseVideos, setBaseVideos] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedBaseIds, setSelectedBaseIds] = useState([]);
  const [talkingPhotoMode, setTalkingPhotoMode] = useState("prompted");
  // Render engine picker (iter68). Magic Hour is intentionally not offered
  // for long-form — see backend note in long_form.py::start_long_form.
  const [talkingPhotoProvider, setTalkingPhotoProvider] = useState("auto");
  const [tone, setTone] = useState("conversational and engaging");
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Ticks every 1s while any job is processing so the ETA badge counts down
  // smoothly instead of only updating on the 8s job-poll.
  const [nowTick, setNowTick] = useState(Date.now());
  // AI-suggested topic list (from /topics/personalized)
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Reference ingestion
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [reference, setReference] = useState(null); // {id, kind: 'link'|'upload', ...}
  const uploadInputRef = React.useRef(null);

  // ─── Bring-your-own-audio mode ──────────────────────────────────────
  // "script" (default) → we generate/take a script + TTS.
  // "audio"            → user uploads their own mp3; script + TTS are skipped.
  const [inputMode, setInputMode] = useState("script");
  const [audioUpload, setAudioUpload] = useState(null); // {audio_id, filename, size_bytes, duration_seconds, estimated_chunks}
  const [audioUploadLoading, setAudioUploadLoading] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);
  const audioInputRef = React.useRef(null);

  const estimatedChunks = Math.max(
    1,
    inputMode === "audio" && audioUpload
      ? Math.ceil(audioUpload.duration_seconds / (pipelineMode === "talking_photo" ? 25 : 55))
      : Math.ceil(targetMinutes)
  );
  const estimatedCost = useMemo(() => {
    // Fast paths (VEED/Fabric) are cheaper per chunk than Magic Hour;
    // the numbers below approximate real fal.ai rates:
    //   Fabric 1.0    ~$0.06/chunk (25s @ 480p)
    //   veed/lipsync  ~$0.02/chunk (55s @ $0.007/s)
    //   Magic Hour    $0.17-0.27/chunk (fallback only)
    const perChunk = inputMode === "audio"
      ? (pipelineMode === "talking_photo" ? 0.06 : 0.02)
      : (pipelineMode === "lip_sync" ? 0.27 : 0.17);
    const mh = perChunk * estimatedChunks;
    const tts = inputMode === "audio" ? 0 : (targetMinutes * 150 / 1000) * 0.015;
    const script = inputMode === "audio" || customScript ? 0 : 0.02;
    return (mh + tts + script).toFixed(2);
  }, [pipelineMode, estimatedChunks, targetMinutes, customScript, inputMode]);

  const load = async () => {
    try {
      const [imgs, bases, js] = await Promise.all([
        axios.get(`${API}/ai-twin/images`),
        axios.get(`${API}/base-videos`),
        axios.get(`${API}/long-form/jobs`),
      ]);
      setImages(imgs.data.images || []);
      setBaseVideos((bases.data.videos || []).filter((b) => (b.status || "ready") === "ready"));
      setJobs(js.data.jobs || []);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // live poll while renders are in flight
    return () => clearInterval(t);
  }, []);

  // Sub-poll ticker: while ANY job is processing, tick every second so the
  // ETA badge visibly counts down between backend polls. Runs cheaply — we
  // exit fast (return null) as soon as no jobs are in flight.
  useEffect(() => {
    const anyProcessing = jobs.some((j) => j.status === "processing");
    if (!anyProcessing) return undefined;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [jobs]);

  const fetchSuggestedTopics = async (refresh = false) => {
    setSuggestLoading(true);
    try {
      const r = await axios.get(`${API}/topics/personalized`, { params: refresh ? { refresh: true } : {} });
      const objs = r.data.topic_objects || (r.data.topics || []).map((t) => ({ topic: t, used: false }));
      setSuggestedTopics(objs);
      setSuggestOpen(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load topic suggestions");
    } finally {
      setSuggestLoading(false);
    }
  };

  const ingestFromLink = async () => {
    if (!linkUrl.trim()) return;
    setLinkLoading(true); setError("");
    try {
      const r = await axios.post(`${API}/reference/from-link`, {
        url: linkUrl.trim(),
        target_minutes: targetMinutes,
      });
      const ref = { ...r.data, kind: "link", url: linkUrl.trim() };
      setReference(ref);
      if (ref.topic) setTopic(ref.topic);
      if (ref.rewritten_script) setCustomScript(ref.rewritten_script);
      setLinkUrl("");
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't pull that link — try another URL");
    } finally {
      setLinkLoading(false);
    }
  };

  const ingestFromUpload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Reference video must be under 50 MB");
      if (uploadInputRef.current) uploadInputRef.current.value = "";
      return;
    }
    setUploadLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/reference/from-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReference({ ...r.data, kind: "upload" });
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't analyze that video — try a shorter clip");
    } finally {
      setUploadLoading(false);
    }
  };

  const clearReference = () => setReference(null);

  const uploadUserAudio = async (file) => {
    if (!file) return;
    if (!/\.mp3$/i.test(file.name)) {
      setError("Please upload an .mp3 file (other formats aren't supported yet).");
      if (audioInputRef.current) audioInputRef.current.value = "";
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError("Audio file must be under 200 MB.");
      if (audioInputRef.current) audioInputRef.current.value = "";
      return;
    }
    setAudioUploadLoading(true); setError(""); setAudioUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/long-form/upload-audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setAudioUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setAudioUpload(r.data);
      // Sync targetMinutes to the actual audio duration so cost & timing
      // estimates line up. Cap at 60 min server-side.
      setTargetMinutes(Math.max(1, Math.min(60, Math.round((r.data.duration_seconds || 60) / 60))));
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't upload that audio file.");
    } finally {
      setAudioUploadLoading(false);
      if (audioInputRef.current) audioInputRef.current.value = "";
    }
  };

  const clearAudioUpload = () => {
    setAudioUpload(null);
    setAudioUploadProgress(0);
  };

  const toggleBaseVideo = (id) => {
    setSelectedBaseIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) return prev; // max 5
      return [...prev, id];
    });
  };

  const handleSubmit = async () => {
    if (inputMode === "audio") {
      if (!audioUpload?.audio_id) { setError("Upload your mp3 first."); return; }
    } else {
      if (!topic.trim() && !customScript.trim()) { setError("Give us a topic or paste your own script."); return; }
    }
    if (pipelineMode === "lip_sync" && selectedBaseIds.length === 0) { setError("Pick at least one base video."); return; }
    if (pipelineMode === "talking_photo" && !selectedImage) { setError("Pick an AI Twin image."); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/long-form/start`, {
        topic: inputMode === "audio" ? "" : topic.trim(),
        script: inputMode === "audio" ? "" : customScript.trim(),
        target_minutes: Number(targetMinutes),
        pipeline_mode: pipelineMode,
        talking_photo_provider: talkingPhotoProvider,
        base_video_ids: pipelineMode === "lip_sync" ? selectedBaseIds : [],
        image_id: pipelineMode === "talking_photo" ? selectedImage?.id : null,
        talking_photo_mode: talkingPhotoMode,
        tone,
        platform: "youtube",
        reference_ingest_id: reference?.id || null,
        audio_id: inputMode === "audio" ? audioUpload?.audio_id : null,
      });
      setTopic(""); setCustomScript("");
      if (inputMode === "audio") clearAudioUpload();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't start — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/long-form/jobs/${id}`);
      await load();
    } catch (e) { /* ignore */ }
  };

  const handleRetry = async (id) => {
    try {
      const { data } = await axios.post(`${API}/long-form/jobs/${id}/retry`);
      if (data?.resumed_chunks > 0) {
        toast.success(`Resuming — ${data.resumed_chunks} chunk${data.resumed_chunks === 1 ? "" : "s"} already done, picking up where we left off.`);
      } else {
        toast.success("Restarting render…");
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't retry job");
    }
  };

  return (
    <div className="space-y-6" data-testid="long-form-studio">
      <div className="card p-6 space-y-2">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#c91a7b] flex items-center justify-center shadow-lg">
            <FilmSlate size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Long-Form Studio</h1>
            <p className="text-sm text-zinc-500 mt-0.5">YouTube-length videos that bypass Magic Hour's 3-minute per-clip cap. We chunk, render, and stitch behind the scenes.</p>
          </div>
        </div>
      </div>

      {/* REFERENCE — "recreate this creator's idea / edit style with my AI Twin" */}
      <div className="card p-5 space-y-4" data-testid="lf-reference-section">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Lightning size={14} weight="fill" className="text-[#E91E8C]" /> Inspiration (optional)</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Saw a reel / YouTube you love? Drop the link — we rewrite it in your voice. Or upload the mp4 and we copy the edit style.</p>
          </div>
          {reference && (
            <button onClick={clearReference} className="text-[11px] font-semibold text-zinc-500 hover:text-red-500 flex items-center gap-1" data-testid="lf-reference-clear">
              <X size={11} weight="bold" /> Clear reference
            </button>
          )}
        </div>

        {!reference ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* From a link */}
            <div className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-bold text-zinc-900 flex items-center gap-1"><LinkIcon size={12} weight="bold" /> From a link</p>
              <p className="text-[10px] text-zinc-500">YouTube · Instagram · TikTok · Twitter · LinkedIn · Facebook · Pinterest</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://www.instagram.com/reel/..."
                  className="input-field text-sm flex-1"
                  data-testid="lf-reference-url"
                />
                <button
                  onClick={ingestFromLink}
                  disabled={linkLoading || !linkUrl.trim()}
                  className="bg-[#E91E8C] hover:bg-[#c91a7b] text-white text-xs font-bold px-3 rounded-md disabled:opacity-40 flex items-center gap-1"
                  data-testid="lf-reference-link-btn"
                >
                  {linkLoading ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                  Pull
                </button>
              </div>
              <p className="text-[10px] text-zinc-400">Cost: ~$0.03 · results the topic, angle, and a fully-rewritten-in-your-voice script.</p>
            </div>

            {/* From an upload (edit recipe) */}
            <div className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-bold text-zinc-900 flex items-center gap-1"><Upload size={12} weight="bold" /> Upload a reel for edit-style copy</p>
              <p className="text-[10px] text-zinc-500">We analyze cuts, zooms, overlays &amp; pacing with Gemini 3 and match them.</p>
              <input
                ref={uploadInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => ingestFromUpload(e.target.files?.[0])}
                data-testid="lf-reference-upload-input"
              />
              <button
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploadLoading}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold py-2 rounded-md disabled:opacity-40 flex items-center justify-center gap-1"
                data-testid="lf-reference-upload-btn"
              >
                {uploadLoading ? <><CircleNotch size={12} className="animate-spin" /> Analyzing with Gemini 3… (30-60s)</> : <><Upload size={12} weight="bold" /> Choose mp4 (≤50 MB, ≤2.5 min)</>}
              </button>
              <p className="text-[10px] text-zinc-400">Cost: ~$0.10-0.20 · results a shot-by-shot edit recipe we apply to your render.</p>
            </div>
          </div>
        ) : (
          <div className="border-2 border-[#E91E8C] bg-pink-50/40 rounded-lg p-4 space-y-2" data-testid="lf-reference-summary">
            {reference.kind === "link" ? (
              <>
                <p className="text-xs font-bold text-zinc-900 flex items-center gap-1"><LinkIcon size={12} weight="bold" className="text-[#E91E8C]" /> Reference loaded · {reference.platform || "link"}</p>
                {reference.title && <p className="text-[11px] text-zinc-600">{reference.title}</p>}
                <p className="text-xs text-zinc-700"><b>Topic:</b> {reference.topic}</p>
                {(reference.angle_notes || []).length > 0 && (
                  <ul className="text-[11px] text-zinc-600 list-disc ml-4 space-y-0.5">
                    {reference.angle_notes.slice(0, 3).map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mt-2"><b>✓</b> Topic and rewritten script pre-filled below. Edit freely.</p>
              </>
            ) : (
              <>
                <p className="text-xs font-bold text-zinc-900 flex items-center gap-1"><Upload size={12} weight="bold" className="text-[#E91E8C]" /> Edit recipe extracted · {reference.duration_seconds?.toFixed(1) || "?"}s · {reference.pacing?.style_tag || "custom"}</p>
                <p className="text-xs text-zinc-700">{reference.summary}</p>
                {(reference.recreate_notes || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mt-2">Recipe notes applied to your render:</p>
                    <ul className="text-[11px] text-zinc-600 list-disc ml-4 space-y-0.5">
                      {reference.recreate_notes.slice(0, 6).map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 mt-1">Shots: {(reference.shots || []).length} · avg shot: {reference.pacing?.avg_shot_seconds?.toFixed?.(1) || "?"}s</p>
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mt-2"><b>✓</b> We'll guide the generated script's pacing + structure to match this edit style.</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Config */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">1. Voice source</h3>
          </div>

          {/* Input-mode toggle: generate from script vs upload your own mp3 */}
          <div className="grid grid-cols-2 gap-2" data-testid="lf-input-mode-toggle">
            <button
              type="button"
              onClick={() => setInputMode("script")}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                inputMode === "script" ? "border-[#E91E8C] bg-pink-50" : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid="lf-mode-script"
            >
              <p className="text-xs font-bold text-zinc-900 flex items-center gap-1.5"><MagicWand size={13} weight="fill" /> Generate script</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">Give us a topic — we write + voice it.</p>
            </button>
            <button
              type="button"
              onClick={() => setInputMode("audio")}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                inputMode === "audio" ? "border-[#E91E8C] bg-pink-50" : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid="lf-mode-audio"
            >
              <p className="text-xs font-bold text-zinc-900 flex items-center gap-1.5"><MusicNote size={13} weight="fill" /> Upload my audio</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">Bring your webinar mp3 — up to 60 min.</p>
            </button>
          </div>

          {inputMode === "script" ? (
            <>
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => fetchSuggestedTopics(suggestOpen)}
                  disabled={suggestLoading}
                  className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] flex items-center gap-1 disabled:opacity-40"
                  data-testid="lf-suggest-topics-btn"
                >
                  {suggestLoading
                    ? <CircleNotch size={11} className="animate-spin" />
                    : suggestOpen ? <ArrowClockwise size={11} weight="bold" /> : <MagicWand size={11} weight="fill" />}
                  {suggestLoading ? "Thinking…" : suggestOpen ? "Refresh topics" : "Suggest topics for me"}
                </button>
              </div>

              {suggestOpen && suggestedTopics.length > 0 && (
                <div className="space-y-2" data-testid="lf-suggested-topics">
                  <p className="text-[11px] text-zinc-500">Tap one to use it — or click <b>Refresh topics</b> for a fresh set.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto pr-1">
                    {suggestedTopics.map((t, i) => (
                      <button
                        key={`${t.topic}-${i}`}
                        type="button"
                        onClick={() => { setTopic(t.topic); setSuggestOpen(false); }}
                        className={`text-left text-xs p-2 rounded-md border transition-all ${
                          topic === t.topic
                            ? "border-[#E91E8C] bg-pink-50 text-zinc-900"
                            : "border-zinc-200 hover:border-[#E91E8C]/40 hover:bg-pink-50/40 text-zinc-700"
                        }`}
                        data-testid={`lf-topic-suggestion-${i}`}
                      >
                        <span className="flex items-center gap-1.5">
                          {t.used && <span className="text-[9px] uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">used</span>}
                          <span className="line-clamp-2">{t.topic}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. How I launched my beauty brand on $2,000"
                className="input-field text-sm"
                data-testid="lf-topic"
              />
              <p className="text-[11px] text-zinc-400">OR paste your full script (optional). If empty we auto-write ~{Math.round(targetMinutes * 150)} words.</p>
              <textarea
                value={customScript}
                onChange={(e) => setCustomScript(e.target.value)}
                rows={4}
                placeholder="Paste a pre-written script here if you already have one."
                className="input-field text-sm resize-none"
                data-testid="lf-script"
              />
            </>
          ) : (
            <div className="space-y-3" data-testid="lf-audio-upload-panel">
              {!audioUpload ? (
                <>
                  <label
                    htmlFor="lf-audio-file-input"
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      audioUploadLoading
                        ? "border-[#E91E8C]/40 bg-pink-50/30"
                        : "border-zinc-300 hover:border-[#E91E8C] hover:bg-pink-50/30"
                    }`}
                    data-testid="lf-audio-dropzone"
                  >
                    <MusicNote size={28} weight="fill" className="mx-auto text-[#E91E8C]" />
                    <p className="mt-2 text-sm font-semibold text-zinc-900">
                      {audioUploadLoading ? `Uploading… ${audioUploadProgress}%` : "Drop your mp3 or click to browse"}
                    </p>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      Up to 60 min · mp3 only · ≤ 200 MB · powered by VEED/Fabric (fast)
                    </p>
                    {audioUploadLoading && (
                      <div className="mt-3 h-1.5 bg-pink-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#E91E8C] transition-all"
                          style={{ width: `${audioUploadProgress}%` }}
                        />
                      </div>
                    )}
                    <input
                      id="lf-audio-file-input"
                      ref={audioInputRef}
                      type="file"
                      accept="audio/mpeg,.mp3"
                      className="hidden"
                      disabled={audioUploadLoading}
                      onChange={(e) => uploadUserAudio(e.target.files?.[0])}
                      data-testid="lf-audio-file-input"
                    />
                  </label>
                  <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2.5">
                    We'll split your audio into ~2:45 chunks and lip-sync each to the base video (or AI Twin) you pick below. Silences and pacing are kept exactly as you recorded them.
                  </p>
                </>
              ) : (
                <div className="border-2 border-[#E91E8C]/30 bg-pink-50/40 rounded-xl p-4" data-testid="lf-audio-uploaded-summary">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-zinc-900 flex items-center gap-1.5 truncate">
                        <MusicNote size={14} weight="fill" className="text-[#E91E8C] flex-shrink-0" />
                        <span className="truncate">{audioUpload.filename}</span>
                      </p>
                      <p className="text-[11px] text-zinc-600 mt-1">
                        {(audioUpload.duration_seconds / 60).toFixed(1)} min · {(audioUpload.size_bytes / 1024 / 1024).toFixed(1)} MB · {audioUpload.estimated_chunks} chunks
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearAudioUpload}
                      className="p-1 text-zinc-400 hover:text-zinc-700 flex-shrink-0"
                      data-testid="lf-audio-clear-btn"
                      aria-label="Remove uploaded audio"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                    <b>✓</b> Ready to lip-sync. Pick a base video (or AI Twin) below, then hit <b>Start render</b>.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card p-5 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">2. Length & pipeline</h3>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">
              {inputMode === "audio" && audioUpload
                ? `Length: ${targetMinutes} min (from your upload · ~${estimatedChunks} chunks)`
                : `Target length: ${targetMinutes} min (~${estimatedChunks} chunks)`}
            </label>
            <input
              type="range"
              min={2}
              max={inputMode === "audio" ? 60 : 30}
              step={1}
              value={targetMinutes}
              onChange={(e) => setTargetMinutes(Number(e.target.value))}
              disabled={inputMode === "audio" && !!audioUpload}
              className="w-full accent-[#E91E8C] disabled:opacity-50"
              data-testid="lf-target-minutes"
            />
            <div className="flex justify-between text-[10px] text-zinc-400"><span>2 min</span><span>{inputMode === "audio" ? "30 min" : "15 min"}</span><span>{inputMode === "audio" ? "60 min" : "30 min"}</span></div>
          </div>
          <div className="flex items-center gap-2">
            {[["lip_sync", "Lip Sync", "Rotates your base videos"], ["talking_photo", "Talking Photo", "Static AI Twin image"]].map(([v, lbl, d]) => (
              <button
                key={v}
                type="button"
                onClick={() => setPipelineMode(v)}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  pipelineMode === v ? "border-[#E91E8C] bg-pink-50" : "border-zinc-200 hover:border-zinc-300"
                }`}
                data-testid={`lf-mode-${v}`}
              >
                <p className="text-sm font-bold text-zinc-900">{lbl}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{d}</p>
              </button>
            ))}
          </div>
          {pipelineMode === "talking_photo" && (
            <>
              <select value={talkingPhotoMode} onChange={(e) => setTalkingPhotoMode(e.target.value)} className="input-field text-sm" data-testid="lf-tp-mode">
                <option value="prompted">Prompted — faster, slightly lower likeness</option>
                <option value="realistic">Realistic — slower, best likeness</option>
              </select>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2" data-testid="lf-tp-warning">
                <WarningCircle size={16} weight="fill" className="flex-shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-bold">Talking Photo is 5–8x slower for long-form</p>
                  <p className="mt-0.5 text-amber-700">Each ~55-sec chunk renders in 25–30 min (vs ~5 min for Lip Sync). A 5-min video = ~3 HOURS total and can hit per-chunk timeouts on busy queue days. Only use this if you don't have a base video yet.</p>
                  <button
                    type="button"
                    onClick={() => setPipelineMode("lip_sync")}
                    className="mt-1.5 text-[11px] font-bold text-amber-900 underline hover:text-amber-700"
                    data-testid="lf-switch-to-lipsync"
                  >
                    ← Switch to Lip Sync
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="pt-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1.5">
              Render engine
            </label>
            <select
              value={talkingPhotoProvider}
              onChange={(e) => setTalkingPhotoProvider(e.target.value)}
              className="input-field text-sm"
              data-testid="lf-provider-picker"
            >
              <option value="auto">Auto — best value (VEED for lip-sync)</option>
              {pipelineMode === "lip_sync" && (
                <option value="veed">VEED Lipsync — fastest &amp; cheapest ($0.24/11-min video)</option>
              )}
              {pipelineMode === "talking_photo" && (
                <option value="heygen">💎 HeyGen HD — premium 1080p avatar (uses more credits)</option>
              )}
            </select>
            <p className="mt-1 text-[10px] text-zinc-400">
              {pipelineMode === "talking_photo"
                ? "HeyGen HD renders each chunk at 1080p (~$0.05/sec) — premium quality, higher cost. Auto uses the fast fal.ai path."
                : "Long-Form Lip Sync renders on fal.ai (VEED). HeyGen HD is available in Talking Photo mode."}
            </p>
          </div>

          <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-2.5">
            Estimated cost: <b className="text-zinc-900">${estimatedCost}</b> · ~{Math.ceil(estimatedChunks * (pipelineMode === "lip_sync" ? 5 : 28))} min render time
          </div>
        </div>
      </div>

      {/* Driver pick */}
      <div className="card p-5 space-y-3" data-testid="lf-driver-section">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">
          3. {pipelineMode === "lip_sync" ? "Base videos (pick up to 5 — we rotate)" : "AI Twin image"}
        </h3>
        {pipelineMode === "lip_sync" ? (
          baseVideos.length === 0 ? (
            <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">
              No base videos yet — generate one under <b>AI Twin Gallery</b> or upload under <b>Credits & Keys → Base Videos</b>.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {baseVideos.map((b) => {
                const picked = selectedBaseIds.includes(b.id);
                const idx = selectedBaseIds.indexOf(b.id);
                return (
                  <button
                    key={b.id}
                    onClick={() => toggleBaseVideo(b.id)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                      picked ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`lf-base-${b.id}`}
                  >
                    <video src={`${BACKEND_URL}/api/base-videos/${b.id}/file`} className="w-full aspect-video object-cover bg-zinc-900 pointer-events-none" muted playsInline preload="metadata" />
                    <div className="p-1.5 bg-white border-t border-zinc-100">
                      <p className="text-[10px] font-medium text-zinc-900 truncate">{b.name || "Base Video"}</p>
                    </div>
                    {picked && (
                      <span className="absolute top-1 left-1 bg-[#E91E8C] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow">#{idx + 1}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )
        ) : (
          images.length === 0 ? (
            <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">No AI Twin images yet — create one in AI Twin Gallery.</p>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
              {images.slice(0, 24).map((img) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImage(img)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${
                    selectedImage?.id === img.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`lf-twin-${img.id}`}
                >
                  <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )
        )}
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="lf-error">{error}</div>}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-[#E91E8C] hover:bg-[#c91a7b] text-white font-bold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="lf-submit-btn"
      >
        {submitting ? <><CircleNotch size={18} className="animate-spin" /> Starting…</> : <><Sparkle size={18} weight="fill" /> Generate long-form video</>}
      </button>

      {/* Jobs history */}
      <div className="card p-5 space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Clock size={14} weight="fill" className="text-[#E91E8C]" /> Your long-form jobs</h3>
        {jobs.length === 0 ? (
          <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">No long-form jobs yet. Your first render will show up here.</p>
        ) : (
          <div className="space-y-2">
            {jobs.map((j) => {
              const chunkProgress = j.chunks ? j.chunks.filter((c) => c.status === "done").length : 0;
              const Icon = j.status === "completed" ? CheckCircle : j.status === "failed" ? WarningCircle : CircleNotch;
              const tone = j.status === "completed" ? "text-emerald-600" : j.status === "failed" ? "text-red-600" : "text-[#E91E8C] animate-spin";

              // ─── ETA for processing jobs (ticks down every 1s) ───────
              let etaLabel = null;
              if (j.status === "processing" && j.total_chunks > 0) {
                const remaining = Math.max(0, j.total_chunks - chunkProgress);
                let secPerChunk;
                if (chunkProgress > 0 && j.created_at) {
                  // Empirical rate — average of chunks completed so far.
                  const elapsedMs = nowTick - new Date(j.created_at).getTime();
                  secPerChunk = Math.max(45, elapsedMs / 1000 / chunkProgress);
                } else {
                  // Fresh job — use per-pipeline heuristic (Fabric ≈ 60s, VEED ≈ 90s).
                  secPerChunk = j.pipeline_mode === "talking_photo" ? 60 : 90;
                }
                let totalSec;
                if (chunkProgress > 0 && j.created_at) {
                  // Anchored to a real timeline: expected-finish = created_at + (total_chunks × secPerChunk) + stitching.
                  const expectedFinishMs = new Date(j.created_at).getTime()
                    + (j.total_chunks * secPerChunk + 60) * 1000;
                  totalSec = Math.max(0, (expectedFinishMs - nowTick) / 1000);
                } else {
                  totalSec = remaining * secPerChunk + 60;
                }
                if (totalSec <= 0) etaLabel = "wrapping up…";
                else if (totalSec < 300) {
                  // mm:ss format for the final 5 minutes so the badge visibly ticks every second.
                  const mins = Math.floor(totalSec / 60);
                  const secs = Math.floor(totalSec % 60);
                  etaLabel = `${mins}:${secs.toString().padStart(2, "0")} left`;
                } else if (totalSec < 3600) etaLabel = `~${Math.ceil(totalSec / 60)} min left`;
                else etaLabel = `~${(totalSec / 3600).toFixed(1)} h left`;
              }

              return (
                <div key={j.id} className="flex items-center gap-3 p-3 border border-zinc-200 rounded-lg" data-testid={`lf-job-${j.id}`}>
                  <Icon size={20} weight="fill" className={tone} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{j.topic || "Long-form"}</p>
                    <p className="text-[11px] text-zinc-500">
                      {j.pipeline_mode === "lip_sync" ? <VideoCamera size={10} weight="fill" className="inline mr-1" /> : <ImageIcon size={10} weight="fill" className="inline mr-1" />}
                      {j.pipeline_mode} · {(Math.round((j.target_minutes || 0) * 10) / 10).toFixed(1)} min · {j.status}
                      {j.status === "processing" && j.total_chunks > 0 && (
                        <> · chunk {chunkProgress}/{j.total_chunks} ({j.current_step || "…"})</>
                      )}
                      {j.actual_cost_usd > 0 && <> · ${j.actual_cost_usd.toFixed(2)}</>}
                    </p>
                    {etaLabel && (
                      <p className="text-[11px] text-[#E91E8C] font-semibold mt-0.5" data-testid={`lf-eta-${j.id}`}>
                        {etaLabel}
                      </p>
                    )}
                    {j.error && <p className="text-[11px] text-red-600 mt-0.5 break-words">{j.error}</p>}
                  </div>
                  {j.status === "completed" && (
                    <a
                      href={`${BACKEND_URL}/api/long-form/jobs/${j.id}/file`}
                      className="text-xs font-semibold text-[#E91E8C] hover:text-[#c91a7b] flex items-center gap-1"
                      data-testid={`lf-download-${j.id}`}
                    >
                      <DownloadSimple size={12} weight="bold" /> Download
                    </a>
                  )}
                  {j.status === "failed" && (
                    <button
                      onClick={() => handleRetry(j.id)}
                      className="text-xs font-semibold text-[#E91E8C] hover:text-[#c91a7b] flex items-center gap-1 border border-[#E91E8C] rounded px-2 py-1"
                      data-testid={`lf-retry-${j.id}`}
                    >
                      <ArrowClockwise size={12} weight="bold" /> Retry
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(j.id)}
                    className="text-zinc-400 hover:text-red-500 p-1"
                    data-testid={`lf-delete-${j.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
